// RegisterForm.jsx
import React, { useState } from "react";
import { z } from "zod";

const registerScheme = z.object({
  name: z.string().min(3, "Name must be at least 3 characters"),
  email: z.string().email("Invalid email address"),
  mobile: z.string().regex(/^[0-9]{10}$/, "Mobile number must be 10 digits"),
  // Require at least one uppercase, one digit and one special character.
  // Min 4 and Max 6 characters (keeps your original min/max idea).
  password: z
    .string()
    .min(4, "Minimum 4 characters")
    .max(6, "Maximum 6 characters")
    .regex(
      /^(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&]).*$/,
      "Need a capital letter, number & special symbol"
    ),
});

const BIN_ID = "690c6a6bae596e708f4805cb"; 
const MASTER_KEY = "$2a$10$vFa4BtnB1Nu1BUL6ukKdC.lLtW5/vwurJX3qGqj0CTcem.7tuWqyW";

function RegisterForm() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    password: "",
  });
  const [errors, setErrors] = useState({});
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");
    setLoading(true);

    const result = registerScheme.safeParse(formData);
    if (!result.success) {
      const newErrors = {};
      result.error.issues.forEach((err) => {
        newErrors[err.path[0]] = err.message;
      });
      setErrors(newErrors);
      setLoading(false);
      return;
    }

    setErrors({});
    setMessage("⌛ Saving Data...");

    try {
      const getRes = await fetch(`https://api.jsonbin.io/v3/b/${BIN_ID}/latest`, {
        headers: { "X-Master-Key": MASTER_KEY, "Content-Type": "application/json" },
      });

      let existingData = [];
      if (getRes.ok) {
        const jsonData = await getRes.json();
        existingData = jsonData.record || [];
      }

      const updatedData = [...existingData, formData];

      const putRes = await fetch(`https://api.jsonbin.io/v3/b/${BIN_ID}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          "X-Master-Key": MASTER_KEY,
        },
        body: JSON.stringify(updatedData),
      });

      if (putRes.ok) {
        setMessage("✔ Registration Successful");
        setFormData({ name: "", email: "", mobile: "", password: "" });
      } else {
        setMessage("❌ Failed to save data. Check Bin ID or Key.");
      }
    } catch (err) {
      console.error(err);
      setMessage("❗ Network or permission error while saving data");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-gradient-to-r from-blue-50 via-white to-blue-50 p-4">
      <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl border border-gray-200 p-8">
        <h2 className="text-2xl font-bold text-gray-800 mb-4 text-center">📝 Register</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <input
              name="name"
              value={formData.name}
              onChange={handleChange}
              placeholder="Enter Name"
              className="w-full border border-gray-300 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300"
              required
            />
            {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
          </div>

          <div>
            <input
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange}
              placeholder="Enter Email"
              className="w-full border border-gray-300 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300"
              required
            />
            {errors.email && <p className="mt-1 text-sm text-red-600">{errors.email}</p>}
          </div>

          <div>
            <input
              name="mobile"
              value={formData.mobile}
              onChange={handleChange}
              placeholder="Enter Mobile Number"
              className="w-full border border-gray-300 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300"
              required
            />
            {errors.mobile && <p className="mt-1 text-sm text-red-600">{errors.mobile}</p>}
          </div>

          <div>
            <input
              name="password"
              type="password"
              value={formData.password}
              onChange={handleChange}
              placeholder="Enter Password"
              className="w-full border border-gray-300 p-3 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-300"
              required
            />
            {errors.password && <p className="mt-1 text-sm text-red-600">{errors.password}</p>}
            <p className="mt-1 text-xs text-gray-500">
              Password must be 4–6 characters, include one uppercase letter, one number and one special character.
            </p>
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full bg-green-500 text-white py-3 rounded-lg font-semibold shadow-md hover:bg-green-600 transition-all disabled:bg-gray-400"
          >
            {loading ? "Saving..." : "Register"}
          </button>

          <div className="text-center mt-1">
            <p className="text-sm text-gray-700">{message}</p>
          </div>
        </form>
      </div>
    </div>
  );
}

export default RegisterForm;
